import { useEffect, useState, type JSX, type ReactNode } from 'react'
import { formatTimeControl } from '../lib/format.js'

interface Record {
  readonly won: number
  readonly lost: number
  readonly drawn: number
}

interface Split extends Record {
  readonly games: number
}

interface Named {
  readonly name: string
  readonly n: number
}

export interface Stats {
  readonly signedIn: boolean
  readonly games: number
  readonly record?: Record
  readonly asWhite?: Split
  readonly asBlack?: Split
  readonly vsHuman?: Split
  readonly vsBot?: Split
  readonly streak?: { readonly kind: 'won' | 'lost' | 'none'; readonly n: number }
  readonly medianPlies?: number
  readonly endings?: readonly Named[]
  readonly timeControls?: readonly Named[]
  readonly openings?: readonly Named[]
  readonly firstMoves?: readonly Named[]
  readonly loosePerGame?: number | null
  readonly activity?: readonly { readonly day: string; readonly n: number }[]
}

const percent = (part: number, whole: number): number =>
  whole === 0 ? 0 : Math.round((part / whole) * 100)

const Panel = ({
  title,
  children,
}: {
  readonly title: string
  readonly children: ReactNode
}): JSX.Element => (
  <section className="rounded-panel bg-panel p-4">
    <h3 className="text-xs font-semibold tracking-wide text-ink-dim uppercase">{title}</h3>
    {children}
  </section>
)

/**
 * Won / drawn / lost as one bar.
 *
 * A bar rather than three numbers because the shape of a record is the thing
 * you actually read — "mostly green" lands before any digit does.
 */
const RecordBar = ({ record }: { readonly record: Record }): JSX.Element => {
  const total = record.won + record.drawn + record.lost
  if (total === 0) return <p className="mt-2 text-sm text-ink-dim">No games yet.</p>

  return (
    <>
      <div className="mt-2 flex h-2.5 overflow-hidden rounded-full bg-panel-raised">
        <span className="bg-accent" style={{ width: `${(record.won / total) * 100}%` }} />
        <span className="bg-ink-dim" style={{ width: `${(record.drawn / total) * 100}%` }} />
        <span className="bg-danger" style={{ width: `${(record.lost / total) * 100}%` }} />
      </div>
      <p className="mt-2 text-sm">
        <span className="font-bold text-accent">{record.won}W</span>{' '}
        <span className="text-ink-dim">{record.drawn}D</span>{' '}
        <span className="font-bold text-danger">{record.lost}L</span>
        <span className="ml-2 text-ink-dim">{percent(record.won, total)}% won</span>
      </p>
    </>
  )
}

const SplitRow = ({ label, split }: { readonly label: string; readonly split: Split }): JSX.Element => {
  const total = split.won + split.drawn + split.lost
  return (
    <div className="flex items-center gap-3 py-1.5">
      <span className="w-20 shrink-0 text-sm text-ink-dim">{label}</span>
      <span className="flex h-2 flex-1 overflow-hidden rounded-full bg-panel-raised">
        {total > 0 && (
          <>
            <span className="bg-accent" style={{ width: `${(split.won / total) * 100}%` }} />
            <span className="bg-ink-dim" style={{ width: `${(split.drawn / total) * 100}%` }} />
            <span className="bg-danger" style={{ width: `${(split.lost / total) * 100}%` }} />
          </>
        )}
      </span>
      <span className="w-16 shrink-0 text-right font-mono text-xs text-ink-dim">
        {total === 0 ? '—' : `${percent(split.won, total)}%`}
      </span>
    </div>
  )
}

const Bars = ({ items }: { readonly items: readonly Named[] }): JSX.Element => {
  const most = Math.max(...items.map((item) => item.n), 1)
  return (
    <ul className="mt-2 space-y-1.5">
      {items.map((item) => (
        <li key={item.name} className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm">{item.name}</span>
          <span className="h-1.5 w-20 shrink-0 overflow-hidden rounded-full bg-panel-raised">
            <span className="block h-full bg-accent" style={{ width: `${(item.n / most) * 100}%` }} />
          </span>
          <span className="w-6 shrink-0 text-right font-mono text-xs text-ink-dim">{item.n}</span>
        </li>
      ))}
    </ul>
  )
}

/**
 * Thirty days of activity as squares, the shape everybody already knows how to
 * read from a contributions graph.
 */
const Activity = ({
  activity,
}: {
  readonly activity: readonly { readonly day: string; readonly n: number }[]
}): JSX.Element => {
  const byDay = new Map(activity.map((entry) => [entry.day, entry.n]))
  const most = Math.max(...activity.map((entry) => entry.n), 1)

  const days = Array.from({ length: 30 }, (_, index) => {
    const date = new Date(Date.now() - (29 - index) * 86_400_000)
    const key = date.toISOString().slice(0, 10)
    return { key, n: byDay.get(key) ?? 0 }
  })

  return (
    <div className="mt-2 flex flex-wrap gap-1">
      {days.map((day) => (
        <span
          key={day.key}
          title={`${day.key}: ${day.n} game${day.n === 1 ? '' : 's'}`}
          className="h-4 w-4 rounded-sm"
          style={{
            backgroundColor:
              day.n === 0 ? 'var(--color-panel-raised)' : 'var(--color-accent)',
            opacity: day.n === 0 ? 1 : 0.35 + 0.65 * (day.n / most),
          }}
        />
      ))}
    </div>
  )
}

const describeEnding = (kind: string): string =>
  ({
    checkmate: 'Checkmate',
    resignation: 'Resignation',
    timeout: 'Ran out of time',
    stalemate: 'Stalemate',
    agreement: 'Agreed draw',
    insufficientMaterial: 'Not enough material',
    fiftyMoveRule: 'Fifty-move rule',
    seventyFiveMoveRule: 'Seventy-five-move rule',
    threefoldRepetition: 'Repetition',
    fivefoldRepetition: 'Repetition',
  })[kind] ?? kind

export const StatsPanels = ({ stats }: { readonly stats: Stats }): JSX.Element | null => {
  if (stats.games === 0 || stats.record === undefined) return null

  const streak = stats.streak
  const openings = stats.openings ?? []

  return (
    <div className="mt-3 space-y-3">
      <Panel title="Record">
        <RecordBar record={stats.record} />
        {streak !== undefined && streak.n >= 2 && (
          <p className="mt-2 text-sm">
            {streak.kind === 'won' ? (
              <span className="font-semibold text-accent">{streak.n} wins in a row</span>
            ) : (
              <span className="font-semibold text-danger">{streak.n} losses in a row</span>
            )}
          </p>
        )}
      </Panel>

      <Panel title="How that splits">
        {stats.asWhite !== undefined && <SplitRow label="As White" split={stats.asWhite} />}
        {stats.asBlack !== undefined && <SplitRow label="As Black" split={stats.asBlack} />}
        {stats.vsHuman !== undefined && stats.vsHuman.games > 0 && (
          <SplitRow label="vs people" split={stats.vsHuman} />
        )}
        {stats.vsBot !== undefined && stats.vsBot.games > 0 && (
          <SplitRow label="vs models" split={stats.vsBot} />
        )}
      </Panel>

      {openings.length > 0 && (
        <Panel title="What you play">
          <Bars items={openings} />
          {(stats.firstMoves ?? []).length > 1 && (
            <p className="mt-3 text-xs text-ink-dim">
              First move:{' '}
              {(stats.firstMoves ?? [])
                .map((move) => `${move.name} (${move.n})`)
                .join(', ')}
            </p>
          )}
        </Panel>
      )}

      <Panel title="Loose pieces">
        {stats.loosePerGame == null ? (
          <p className="mt-2 text-sm text-ink-dim">Not measured on your games yet.</p>
        ) : (
          <>
            <p className="mt-2 text-2xl font-bold">{stats.loosePerGame.toFixed(1)}</p>
            <p className="mt-1 text-xs leading-relaxed text-ink-dim">
              Moves per game after which you had a piece attacked and defended by nothing. This is a
              plain fact about the position, counted exactly — not an engine evaluation, and not an
              accuracy score. Leaving pieces loose is how most games at this level are actually lost.
            </p>
          </>
        )}
      </Panel>

      <div className="grid gap-3 sm:grid-cols-2">
        {(stats.endings ?? []).length > 0 && (
          <Panel title="How they end">
            <Bars items={(stats.endings ?? []).map((e) => ({ ...e, name: describeEnding(e.name) }))} />
          </Panel>
        )}
        {(stats.timeControls ?? []).length > 0 && (
          <Panel title="Time controls">
            <Bars
              items={(stats.timeControls ?? []).map((tc) => ({
                ...tc,
                name: formatTimeControl(tc.name),
              }))}
            />
          </Panel>
        )}
      </div>

      {(stats.activity ?? []).length > 0 && (
        <Panel title="Last 30 days">
          <Activity activity={stats.activity ?? []} />
          <p className="mt-2 text-xs text-ink-dim">
            Typical game: {Math.round((stats.medianPlies ?? 0) / 2)} moves.
          </p>
        </Panel>
      )}
    </div>
  )
}

/** Fetches and renders, or renders nothing at all for a guest. */
export const useStats = (signedIn: boolean): Stats | null => {
  const [stats, setStats] = useState<Stats | null>(null)

  useEffect(() => {
    if (!signedIn) {
      setStats(null)
      return
    }
    let cancelled = false
    void (async (): Promise<void> => {
      try {
        const response = await fetch('/api/me/stats', { credentials: 'same-origin' })
        const body = (await response.json()) as Stats
        if (!cancelled) setStats(body)
      } catch {
        /* The history list below still works; nothing to say. */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [signedIn])

  return stats
}
